// Affichage des permissions effectives de la session. Le serveur reste autoritaire.
export const ROLES = {
  ADMIN: 'Administrateur',
  BASCULE: 'Responsable Pont-Bascule',
  ATELIER: 'Chef d’Atelier',
  COURSIER: 'Coursier',
  COMPTABLE: 'Comptable',
};

// Modules de l'ERP : la clé sert à la fois au menu, aux routes et aux droits.
export const MODULES = [
  { key: 'dashboard', to: '/', label: 'Vue d’ensemble', emoji: '📊' },
  { key: 'reception', to: '/reception', label: 'Réception', emoji: '⚖️' },
  { key: 'trituration', to: '/trituration', label: 'Trituration', emoji: '🏭' },
  { key: 'planning', to: '/planning', label: 'Planning atelier', emoji: '📋' },
  { key: 'stocks', to: '/stocks', label: 'Stocks & Cuves', emoji: '🛢️' },
  { key: 'achats-huile', to: '/achats-huile', label: 'Achats d’Huile', emoji: '🚛' },
  { key: 'sous-produits', to: '/sous-produits', label: 'Sous-Produits', emoji: '♻️' },
  { key: 'reporting-finance', to: '/reporting-finance', label: 'Finance & Comptabilité', emoji: '📒' },
  { key: 'reporting-finance-config', label: 'Finance : configurer et classer', hidden: true },
  { key: 'reporting-finance-validation', label: 'Finance : valider les écritures préparatoires', hidden: true },
  { key: 'finances', to: '/finances', label: 'Caisse & Finances', emoji: '💰' },
  { key: 'facturier', to: '/facturier', label: 'Facturation & Documents', emoji: '🗂️' },
  { key: 'depenses', to: '/depenses', label: 'Dépenses', emoji: '🧾' },
  { key: 'categories-depenses', to: '/categories-depenses', label: 'Catégories de dépenses', emoji: '🏷️' },
  { key: 'frais-generaux', to: '/frais-generaux', label: 'Dépenses & Frais Généraux', emoji: '📑' },
  { key: 'tresorerie', to: '/tresorerie', label: 'Trésorerie & Financements', emoji: '🏦' },
  { key: 'commercial-whatsapp', to: '/commercial-whatsapp', label: 'Propositions Commerciales & WhatsApp', emoji: '💬' },
  { key: 'prospection-asie', to: '/prospection-asie', label: 'Prospection Export & Tunisie', emoji: '🌏' },
  { key: 'validations', to: '/validations', label: 'Validations coursier', emoji: '✅' },
  { key: 'contacts', to: '/contacts', label: 'Contacts', emoji: '👥' },
  { key: 'pointage-paie', to: '/pointage-paie', label: 'Pointage & Paie', emoji: '👷' },
  { key: 'ventes', to: '/ventes', label: 'Ventes & Comptoir', emoji: '🧾' },
  { key: 'releves', to: '/releves', label: 'Relevés de comptes', emoji: '📄' },
  { key: 'copilote', to: '/copilote', label: 'Copilote IA', emoji: '🤖' },
  { key: 'scan', to: '/scan', label: 'Scan mobile', emoji: '📷' },
  { key: 'passerelle', to: '/passerelle', label: 'Passerelle Drive', emoji: '📥' },
  { key: 'bourse', to: '/bourse', label: 'Bourse de l’Huile', emoji: '📈' },
  { key: 'export', to: '/export', label: 'Export', emoji: '🌍' },
  { key: 'cri', to: '/cout-revient', label: 'Coût de Revient (CRI)', emoji: '🏭' },
  { key: 'coffre', to: '/coffre', label: 'Centre Documentaire', emoji: '🗄️' },
];

// Matrice des droits par profil métier : modules accessibles + capacités.
// Surcharge par utilisateur (Administration › Utilisateurs) appliquée via appliquerSession().
let sessionModules = null;
export const appliquerSession = (session) => {
  sessionModules = Array.isArray(session?.modules) ? [...session.modules] : null;
};

const PROFILS = {
  [ROLES.ADMIN]: { modules: '*', montants: true, admin: true },
  [ROLES.BASCULE]: {
    modules: ['reception', 'planning', 'achats-huile', 'ventes', 'finances', 'facturier', 'depenses', 'frais-generaux', 'contacts', 'pointage-paie', 'releves', 'copilote', 'scan'],
    montants: true,
    restrictions: { 'pointage-paie': { soldes: false } },
  },
  [ROLES.ATELIER]: { modules: ['trituration', 'planning', 'stocks', 'sous-produits'], montants: false },
  [ROLES.COMPTABLE]: { modules: ['finances', 'facturier', 'depenses', 'frais-generaux', 'tresorerie', 'releves', 'contacts', 'passerelle', 'coffre', 'cri', 'dashboard'], montants: true },
  [ROLES.COURSIER]: { modules: ['depenses', 'scan'], montants: false },
};

export const peut = (role, moduleKey, action = 'voir') => {
  const p = PROFILS[role];
  if (!p) return false;
  const accessibles = sessionModules ?? p.modules;
  if (!(accessibles === '*' || accessibles.includes(moduleKey))) return false;
  const specifique = p.restrictions?.[moduleKey];
  if (specifique && action in specifique) return Boolean(specifique[action]);
  if (action === 'voir' || action === 'saisir') return true;
  if (action === 'montants') return Boolean(p.montants);
  if (action === 'override' || action === 'soldes') return Boolean(p.admin);
  return false;
};

export const estAdmin = (role) => Boolean(PROFILS[role]?.admin);
// Caisse imposée : le poste Pont-Bascule encaisse et paie UNIQUEMENT via sa propre caisse.
export const caisseImposee = (role) => (role === ROLES.BASCULE ? 'chq' : null);
export const voitMontants = (role) => Boolean(PROFILS[role]?.montants);
export const modulesVisibles = (role) => MODULES.filter((m) => !m.hidden && peut(role, m.key));
export const moduleAccueil = (role) => modulesVisibles(role)[0]?.to ?? '/acces-limite';

// Modules cochés par défaut pour un profil (base de la fiche utilisateur).
export const modulesParDefaut = (role) => {
  const p = PROFILS[role];
  if (!p) return [];
  return p.modules === '*' ? MODULES.map((m) => m.key) : [...p.modules];
};
